/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';
import { DialogDefaultV2 } from '../../../components/v2';

const modeloDoGrupo = (row) =>
  row.modelo || row.tipoEquipamentoNome || row.tipoEquipamento || 'Sem modelo';

const EscolherPatrimoniosDialog = ({
  open,
  onClose,
  onConfirm,
  grupos,
  loading,
  error,
  quantidadePedida,
  selectedIds,
  itemNome,
}) => {
  const [draftIds, setDraftIds] = useState([]);
  const [localError, setLocalError] = useState(false);

  useEffect(() => {
    if (!open) {
      setDraftIds([]);
      setLocalError(false);
      return;
    }
    setDraftIds(selectedIds || []);
    setLocalError(false);
  }, [open, selectedIds]);

  const toggleUnidade = (unidadeId) => {
    setDraftIds((current) => {
      if (current.includes(unidadeId)) {
        return current.filter((id) => id !== unidadeId);
      }
      if (quantidadePedida > 0 && current.length >= quantidadePedida) {
        return current;
      }
      return [...current, unidadeId];
    });
    setLocalError(false);
  };

  const handleConfirm = () => {
    if (draftIds.length !== quantidadePedida) {
      setLocalError(true);
      return;
    }
    onConfirm(draftIds);
  };

  const disabledConfirm = loading || error || !grupos.length;

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={handleConfirm}
      title={itemNome ? `Escolher equipamentos - ${itemNome}` : 'Escolher equipamentos'}
      confirmMessage="Usar seleção"
      cancelMessage="Voltar"
      disabledConfirmButton={disabledConfirm}
      maxWidth="sm"
      fullWidth
      useFullScreen={false}
    >
      <Stack spacing={1.5}>
        <Typography variant="body2">
          Selecione {quantidadePedida} equipamento
          {quantidadePedida === 1 ? '' : 's'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Selecionados: {draftIds.length}/{quantidadePedida}
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Carregando equipamentos disponíveis…
          </Typography>
        ) : null}
        {error ? (
          <Typography variant="body2" color="error.main">
            Não foi possível carregar os equipamentos. Volte e tente de novo.
          </Typography>
        ) : null}
        {!loading && !error && !grupos.length ? (
          <Typography variant="body2" color="warning.main">
            Não há unidades livres deste tipo com a Eletropontes.
          </Typography>
        ) : null}
        {!loading && !error && grupos.length ? (
          <Stack
            spacing={1.5}
            sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}
          >
            {grupos.map((row) => {
              const modelo = modeloDoGrupo(row);
              return (
                <Box key={modelo}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5, color: 'text.primary' }}
                  >
                    {modelo}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.75 }}
                    >
                      ({row.unidades.length})
                    </Typography>
                  </Typography>
                  <Stack spacing={0.25}>
                    {row.unidades.map((unidade) => {
                      const checked = draftIds.includes(unidade.id);
                      const disabled = !checked
                        && quantidadePedida > 0
                        && draftIds.length >= quantidadePedida;
                      return (
                        <FormControlLabel
                          key={unidade.id}
                          control={(
                            <Checkbox
                              size="small"
                              checked={checked}
                              disabled={disabled}
                              onChange={() => toggleUnidade(unidade.id)}
                            />
                          )}
                          label={(
                            <Typography variant="body2">
                              Patrimônio {unidade.numPatrimonio || '—'}
                              {unidade.numSerie
                                ? ` · Série ${unidade.numSerie}`
                                : ''}
                            </Typography>
                          )}
                          sx={{ ml: 0, mr: 0 }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : null}
        {localError ? (
          <FormHelperText error>
            Selecione exatamente {quantidadePedida} patrimônio
            {quantidadePedida === 1 ? '' : 's'}.
          </FormHelperText>
        ) : null}
      </Stack>
    </DialogDefaultV2>
  );
};

EscolherPatrimoniosDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  grupos: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  quantidadePedida: PropTypes.number,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  itemNome: PropTypes.string,
};

EscolherPatrimoniosDialog.defaultProps = {
  open: false,
  grupos: [],
  loading: false,
  error: false,
  quantidadePedida: 0,
  selectedIds: [],
  itemNome: '',
};

export default EscolherPatrimoniosDialog;
